import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SKIP_TARGETS,
  getSkipTarget,
  setSkip,
  setSkipTarget,
} from './skipStart';
import './StartPage.css';

/**
 * Inventory Registry — the Start Menu.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/start-menu.ejs`. Every
 * heading, description and tile caption below is the source's, verbatim — this screen is
 * how the product explains its own method, and paraphrasing it would be rewriting the
 * method.
 *
 * THE ONE STRUCTURAL PROBLEM, AND HOW IT IS HANDLED. The source is ONE application, so
 * its five-step journey links freely from scoring all the way through to delivery
 * tracking. Here those steps span several PRODUCTS: steps 4 and 5 are Program Planning
 * and Program Management, which are separate modules on this platform (and Program
 * Management is piece 2 of this very port).
 *
 * Dropping those steps would misdescribe the method — the journey is five steps and
 * showing three would say the product does less than it does. Linking them to routes that
 * do not exist would be worse. So every step and every tile is present with its original
 * wording, and a tile whose destination is not part of Inventory Registry renders in a
 * dimmed, non-clickable state that says where it actually lives. Raised with the owner
 * rather than decided silently.
 */

interface Tool {
  icon: string;
  title: string;
  sub: string;
  /** Null when the destination is not a Inventory Registry screen. */
  href: string | null;
  /** Shown on a dimmed tile, naming where the screen really lives. */
  elsewhere?: string;
}

interface Step {
  n: number;
  title: string;
  meth?: string;
  desc: string;
  tools: Tool[];
}

const P = '/portfolio_definition';

const STEPS: Step[] = [
  {
    n: 1,
    title: 'Score — objectively',
    desc: 'Capture every initiative and score it against one consistent framework, so priority reflects evidence, not the loudest voice in the room. You get an unbiased ranking of what’s genuinely worth doing.',
    tools: [
      {
        icon: '💡',
        title: 'Opportunities',
        sub: 'Capture & score initiatives',
        href: `${P}/opportunities/opportunity-registry`,
      },
      {
        icon: '🎚️',
        title: 'Scoring Framework',
        sub: 'Dimensions, criteria & weights',
        href: `${P}/settings/assessment-framework/frameworks`,
      },
      {
        icon: '🧭',
        title: 'Strategic Intelligence',
        sub: 'Discovery & unbiased analysis',
        href: null,
        elsewhere: 'Lives in the AI Readiness product',
      },
    ],
  },
  {
    n: 2,
    title: 'Weigh the benefit',
    desc: 'Put a number on the business value each opportunity delivers — savings, ROI, and the value it realizes over time — so a go/no-go is defensible rather than a gut call.',
    tools: [
      {
        icon: '💰',
        title: 'Benefits & ROI',
        sub: 'Value & ROI rollups',
        href: null,
        elsewhere: 'Effort & Value is not part of this product',
      },
      {
        icon: '🥧',
        title: 'Portfolio Optimizer',
        sub: 'Value scenarios & trade-offs',
        href: `${P}/portfolio-optimizer/strategy-and-constraints`,
      },
    ],
  },
  {
    n: 3,
    title: 'Weigh the cost',
    desc: 'Model what each initiative costs to build and to run — MVP cost, discounted cash flow, and the resource economics per phase — then set benefit against cost to decide the disposition.',
    tools: [
      {
        icon: '🧮',
        title: 'MVP Cost',
        sub: 'Build-cost scenarios',
        href: `${P}/financial-modeling/configuration`,
      },
      {
        icon: '🏦',
        title: 'NPV Model',
        sub: 'Discounted cash flow',
        href: `${P}/financial-modeling/npv-value-model/npv-cost-scenarios`,
      },
      {
        icon: '⚙️',
        title: 'Delivery Economics',
        sub: 'Cost per phase & mix',
        href: null,
        elsewhere: 'Effort & Value is not part of this product',
      },
      {
        icon: '⚖️',
        title: 'Disposition',
        sub: 'Benefit vs. cost, decided',
        href: null,
        elsewhere: 'Disposition Control is not part of this product',
      },
    ],
  },
  {
    n: 4,
    title: 'Shape the roadmap',
    desc: 'Sequence the work you’ve chosen into a high-level implementation roadmap — phases, dependencies and timelines — and see the whole portfolio’s shape before you commit a single team.',
    tools: [
      {
        icon: '🗺️',
        title: 'Roadmap Planning',
        sub: 'Sequence phases & dependencies',
        href: null,
        elsewhere: 'Lives in the Program Planning & Roadmap product',
      },
      {
        icon: '📊',
        title: 'Planning Dashboard',
        sub: 'Score, cost & value at a glance',
        href: null,
        elsewhere: 'Lives in the Reporting product',
      },
    ],
  },
  {
    n: 5,
    title: 'Run delivery',
    meth: 'Agile or Waterfall',
    desc: 'Take the roadmap into delivery and run it your way — define the programs, lock the timeline, then track progress, budget and effort against plan, all the way to sign-off.',
    tools: [
      {
        icon: '🌳',
        title: 'Roadmap Definition',
        sub: 'Programs, opportunities & phases',
        href: null,
        elsewhere: 'Lives in Project Execution & Project Management',
      },
      {
        icon: '📅',
        title: 'Timeline Finalization',
        sub: 'Lock phase dates',
        href: null,
        elsewhere: 'Lives in Project Execution & Project Management',
      },
      {
        icon: '🚀',
        title: 'Execution Control Center',
        sub: 'Progress & delivery status',
        href: null,
        elsewhere: 'Lives in Project Execution & Project Management',
      },
      {
        icon: '📟',
        title: 'Execution Dashboard',
        sub: 'Budget, effort & delivery health',
        href: null,
        elsewhere: 'Lives in the Reporting product',
      },
    ],
  },
];

export interface PortfolioDefinitionStartPageProps {
  firstName?: string;
}

export function PortfolioDefinitionStartPage({ firstName }: PortfolioDefinitionStartPageProps) {
  const navigate = useNavigate();
  // Step 1 opens by default, as the source does (`class="step open"` on the first).
  const [openStep, setOpenStep] = useState<number | null>(1);
  const [path, setPath] = useState<'A' | 'B'>('A');
  const [dest, setDest] = useState<string>(getSkipTarget());

  const go = (to: string | null) => {
    if (to) navigate(to);
  };

  const chooseDest = (value: string) => {
    setDest(value);
    setSkipTarget(value);
  };

  const dontShow = () => {
    setSkip(true);
    setSkipTarget(dest);
    navigate(dest);
  };

  return (
    <div className="pds-wrap">
      <div className="pds-inner">
        <div className="pds-topbar">
          <div className="pds-brand">
            <span className="pds-dot" />
            {/* The source's brand line reads "AI Readiness" because that is the source
                application. Inside this platform the product is Opportunity Registry, and
                the other name here would simply be wrong. */}
            Opportunity Registry
          </div>
          <div className="pds-controls">
            <label className="pds-ctl pds-selctl" title="Choose where Skip and login take you">
              Skip to
              <select
                className="pds-skipsel"
                value={dest}
                onChange={(e) => chooseDest(e.target.value)}
              >
                {SKIP_TARGETS.map((t) => (
                  <option key={t.path} value={t.path}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="pds-ctl pds-skip" onClick={() => navigate(dest)}>
              Go →
            </button>
            <button
              type="button"
              className="pds-ctl"
              title="Skip this menu on future logins and go straight to the app"
              onClick={dontShow}
            >
              Don&apos;t show at login
            </button>
          </div>
        </div>

        <div className="pds-greet">Welcome back{firstName ? `, ${firstName}` : ''}</div>
        <h1>
          From raw idea to <span className="pds-thin">running delivery.</span>
        </h1>
        <p className="pds-lede">
          Pick how you want to work. Everything else follows one honest line — score what
          matters, weigh its value and cost, shape a roadmap, then deliver it. No wall of
          buttons to hunt through.
        </p>

        <div className="pds-fork">
          <button
            type="button"
            className={`pds-path pds-a${path === 'A' ? ' pds-on' : ''}`}
            onClick={() => setPath('A')}
          >
            <div className="pds-p-tag">
              <span className="pds-ico">✦</span>
              The full journey
            </div>
            <div className="pds-p-title">Start from scratch</div>
            <div className="pds-p-when">You have opportunities, but no plan yet.</div>
            <p className="pds-p-desc">
              Let the platform score your initiatives objectively, quantify the benefit and
              the cost, shape a high-level roadmap, then take it into delivery.
            </p>
            <span className="pds-p-cta">
              Follow the five steps <span className="pds-ar">↓</span>
            </span>
          </button>

          <button
            type="button"
            className={`pds-path pds-b${path === 'B' ? ' pds-on' : ''}`}
            onClick={() => setPath('B')}
          >
            <div className="pds-p-tag">
              <span className="pds-ico">⤓</span>
              The express lane
            </div>
            <div className="pds-p-title">Bring your own plan</div>
            <div className="pds-p-when">You already have a finalized roadmap.</div>
            <p className="pds-p-desc">
              Skip the analysis. Import your Waterfall or Agile plan and go straight to
              execution — define delivery, track it, report it.
            </p>
            <span className="pds-p-cta">
              Import &amp; execute <span className="pds-ar">→</span>
            </span>
          </button>
        </div>

        <div className="pds-switchnote">
          Not sure? <b>Start from scratch</b> is the complete method. You can switch an
          opportunity to the express lane any time before it enters delivery.
        </div>

        {path === 'A' ? (
          <>
            <div className="pds-jhead">
              <span className="pds-t">The full journey</span>
              <span className="pds-rail" />
              <span className="pds-count">5 steps</span>
            </div>

            <div className="pds-steps">
              {STEPS.map((s) => (
                <div key={s.n} className={`pds-step${openStep === s.n ? ' pds-open' : ''}`}>
                  <button
                    type="button"
                    className="pds-s-head"
                    aria-expanded={openStep === s.n}
                    onClick={() => setOpenStep(openStep === s.n ? null : s.n)}
                  >
                    <span className="pds-s-num">{s.n}</span>
                    <span className="pds-s-main">
                      <span className="pds-s-title">
                        {s.title}
                        {s.meth ? <span className="pds-meth">{s.meth}</span> : null}
                      </span>
                      <span className="pds-s-desc">{s.desc}</span>
                    </span>
                    <span className="pds-s-caret">›</span>
                  </button>
                  <div className="pds-s-body">
                    <div className="pds-s-inner">
                      <p className="pds-s-lbl">What&apos;s in here</p>
                      <div className="pds-tools">
                        {s.tools.map((t) => (
                          <button
                            key={t.title}
                            type="button"
                            className={`pds-tool${t.href ? '' : ' pds-elsewhere'}`}
                            title={t.elsewhere ?? undefined}
                            disabled={!t.href}
                            onClick={() => go(t.href)}
                          >
                            <span className="pds-ti">{t.icon}</span>
                            <span>
                              <span className="pds-tt">{t.title}</span>
                              <span className="pds-td">{t.elsewhere ?? t.sub}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="pds-jhead">
              <span className="pds-t">The express lane</span>
              <span className="pds-rail" />
              <span className="pds-count">2 steps</span>
            </div>
            <div className="pds-express">
              <h3>You already have the plan — let&apos;s execute it.</h3>
              <p>
                No scoring, no analysis. Import a finalized roadmap (Waterfall phases or
                Agile MVPs &amp; sprints) from Excel, and the delivery program is created
                for you. From there it&apos;s the same control center as everyone else.
              </p>
              <div className="pds-exsteps">
                {/*
                  Both express-lane steps land in Project Execution & Project Management —
                  piece 2 of this port. Program Quick Start is this product's own import
                  path and is offered first, because it is the one that exists today.
                */}
                <button
                  type="button"
                  className="pds-exstep"
                  onClick={() => navigate(`${P}/program-quick-start`)}
                >
                  <div className="pds-en">Step 1</div>
                  <div className="pds-et">Import your roadmap</div>
                  <div className="pds-ed">
                    Upload a client plan on Program Quick Start — the program, its
                    opportunities, phases and timeline are proposed for you in one shot.
                  </div>
                </button>
                <button type="button" className="pds-exstep pds-elsewhere" disabled>
                  <div className="pds-en">Step 2</div>
                  <div className="pds-et">Run delivery</div>
                  <div className="pds-ed">
                    Track progress, budget and effort in the Execution Control Center, and
                    report status — Agile or Waterfall. Lives in Project Execution &amp;
                    Project Management.
                  </div>
                </button>
              </div>
            </div>
          </>
        )}

        <div className="pds-foot">
          <button type="button" onClick={() => navigate(`${P}/user-guide`)}>
            User Guide
          </button>
          <button type="button" onClick={() => navigate(`${P}/help-support`)}>
            Help &amp; Support
          </button>
        </div>
      </div>
    </div>
  );
}
